/**
 * Navigation Module Types
 *
 * Defines the types of navigation contexts for bidirectional
 * navigation between Viewer and Non-Viewer modules.
 */

/**
 * Navigation module types for history tracking
 */
export const MODULE_TYPES = {
  VIEWER: 'viewer',
  NON_VIEWER: 'non-viewer'
}

/**
 * Route names that are considered "Viewer" routes
 * These are the primary 3D/360 viewer interfaces
 */
export const VIEWER_ROUTES = ['facility', 'RealityViewer']

/**
 * Check if a route name is a viewer route
 * @param {string} routeName - Route name to check
 * @returns {boolean}
 */
export function isViewerRoute(routeName) {
  return VIEWER_ROUTES.includes(routeName)
}

/**
 * Get module type from route name
 * @param {string} routeName - Route name
 * @returns {string} MODULE_TYPES.VIEWER or MODULE_TYPES.NON_VIEWER
 */
export function getModuleType(routeName) {
  return isViewerRoute(routeName) ? MODULE_TYPES.VIEWER : MODULE_TYPES.NON_VIEWER
}

/**
 * Format route name to human-readable label
 * Transforms camelCase/PascalCase/kebab-case to readable text
 *
 * @param {string} name - Route name
 * @returns {string} Formatted label
 *
 * @example
 * formatRouteLabel('TagsRegisterView') // 'Tags Register'
 * formatRouteLabel('facility') // 'Facility'
 * formatRouteLabel('task-board') // 'Task Board'
 */
export function formatRouteLabel(name) {
  if (!name) return 'Previous'

  return name
    // Insert space before capital letters (PascalCase/camelCase)
    .replace(/([A-Z])/g, ' $1')
    // Replace hyphens/underscores with spaces (kebab-case/snake_case)
    .replace(/[-_]/g, ' ')
    // Remove common suffixes
    .replace(/view$/i, '')
    .replace(/register$/i, 'Register')
    // Clean up and format
    .trim()
    .split(' ')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Generate enriched navigation label with specific register/task type names
 * Format: "View Type (Specific Name)" e.g., "Kanban Board (Equipment Inspections)"
 *
 * This function looks up task type names and register names from the Vuex store
 * to provide more descriptive navigation labels than the generic route name.
 *
 * @param {string} routeName - The route name (e.g., 'kanban', 'RegisterView')
 * @param {Object} params - Route params containing registerId or taskTypeId
 * @param {Object} rootGetters - Vuex rootGetters for store access
 * @returns {string} Formatted label with specific name if available
 *
 * @example
 * resolveNavigationLabel('kanban', { taskTypeId: 'abc123' }, rootGetters)
 * // Returns: "Kanban Board (Equipment Inspections)" if task type exists
 * // Returns: "Kanban Board" if task type not found
 *
 * @example
 * resolveNavigationLabel('RegisterView', { registerId: 'xyz456' }, rootGetters)
 * // Returns: "Register (Tags Register)" if register exists
 * // Returns: "Register" if register not found
 */
export function resolveNavigationLabel(routeName, params, rootGetters) {
  // Task board routes - show task type name
  const taskViewLabels = {
    'kanban': 'Kanban Board',
    'gantt': 'Gantt Chart',
    'schedule': 'Schedule',
    'list': 'List View',
    'task-board-charts': 'Task Charts'
  }

  if (taskViewLabels[routeName]) {
    if (params?.taskTypeId) {
      const taskTypes = rootGetters?.['TASK_MANAGER/GET_ENTRIES'] || []
      const taskType = taskTypes.find(t => t.id === params.taskTypeId)
      const viewLabel = taskViewLabels[routeName]
      return taskType?.name ? `${viewLabel} (${taskType.name})` : viewLabel
    }
    // Route matches but no taskTypeId - return base label
    return taskViewLabels[routeName]
  }

  // Register routes - show register name
  const registerRoutes = ['RegisterView', 'tags-register', 'TagsRegisterView']
  if (registerRoutes.includes(routeName)) {
    if (params?.registerId) {
      const registers = rootGetters?.['REGISTERS/GET_REGISTERS'] || []
      const register = registers.find(r => r.id === params.registerId)
      return register?.name ? `Register (${register.name})` : 'Register'
    }
    // Route matches but no registerId - return base label
    return 'Register'
  }

  // Route doesn't need enrichment - return null to use fallback
  return null
}
